/**
 * Синхронизация каталога поставщиков со сменой клиента и режима доступа (CAB-696).
 *
 * Каталог кэширован в памяти сразу в нескольких сторах (настройки поставщиков,
 * «Подбор», выбор поставщиков во вкладках проценки), и каждый из них перечитывает его
 * на своих событиях — монтирование страницы, закрытие попапа. А смениться каталог
 * умеет молча и под уже открытой страницей: выход из демо-доступа забирает семь
 * демо-поставщиков и даёт своих. Отсюда весь класс дефектов «вижу старый список, F5
 * — вижу новый»: страница, смонтированная до перехода, второй раз не монтируется
 * никогда, и до неё правда просто не доезжает.
 *
 * Поэтому признак ОДИН и точка сброса ОДНА: личность клиента плюс режим доступа. Что
 * бы ни изменило их — своя кнопка «перейти в рабочий режим», вторая вкладка, правка
 * на стороне LH, — кэши каталога перечитываются здесь, а не в каждом экране по
 * отдельности.
 *
 * Новый кэш каталога поставщиков в кабинете — добавлять сюда, в `resyncSupplierCatalog`.
 */

import { useIdentityChange } from "../useIdentityChange";
import { useFunctionSettingsStore } from "../../../store/eplex/eplexFunctionSettings";
import { useSelectionStore } from "../../../store/eplex/eplexSelection";
import { useSingleSearchStore } from "../../../store/eplex/eplexSingleSearch";
import { pruneSavedPluginIds } from "../../helpers/eplex/pluginsStorageHelper";

/** Перечитывает каталог во всех кэшах и приводит к нему сохранённый выбор поставщиков. */
export async function resyncSupplierCatalog(): Promise<void> {
  const functionSettings = useFunctionSettingsStore();
  const selection = useSelectionStore();
  const singleSearch = useSingleSearchStore();

  // Сначала каталог, потом всё, что от него зависит: список доступных id берём уже
  // свежий, иначе выбор сверялся бы с тем же демо-набором, из которого и приехал
  await functionSettings.refreshPlugins();

  const availableIds = functionSettings.userPlugins
    .map((plugin) => Number(plugin.id))
    .filter((id) => Number.isFinite(id) && id > 0);

  pruneSavedPluginIds(availableIds);
  singleSearch.revalidateSelectedPlugins(availableIds);

  // «Подбор» держит свой каталог (склады + прайсы + поставщики) и второй раз его не
  // грузит — сбрасываем, чтобы загрузка состоялась; она же почистит выбор мастера
  selection.resetPluginCatalog();
  await selection.loadPlugins();
}

/**
 * Ставит наблюдение на время жизни кабинета. Вызывается один раз из `MainLayout` —
 * он обёртка всех авторизованных страниц, поэтому наблюдение живёт независимо от
 * того, какой экран открыт в момент смены режима.
 */
export function useSupplierCatalogSync(): void {
  // Признак личности и правило «первая загрузка — не смена» общие для всего, что
  // привязано к человеку: страницы на первой загрузке читают каталог сами, на
  // монтировании
  useIdentityChange(() => {
    resyncSupplierCatalog().catch((error: unknown) => {
      console.error("Supplier catalog resync error: ", error);
    });
  });
}
